'use client';

import { useStore } from 'zustand';
import { useRouter } from 'next/navigation';

import { Button } from '@/components/ui/button/button.component';
import { ButtonVariant } from '@/components/ui/button/button.types';
import { Link } from '@/components/ui/link/link.component';
import { authStore } from '@/store/auth.store';
import { setOpenSignInModal } from '@/store/modal';
import { styleFaq } from '@/app/(main)/(pages)/(institucional)/preciso-de-ajuda/_consts/style-faq.consts';
import { useFeatureFlagContext } from '@/store/feature-flag.store';
import { config } from '@/config/config';
import { useAgendamentoNavigation } from '@/hooks/navigation/use-agendamento-navigation.hook';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';

interface FaqLoginButtonProps {
  label?: string;
  variant?: ButtonVariant;
  className?: string;
}

export const FaqLoginButton = ({
  label = 'Meu cadastro online',
  variant = 'secondary',
  className,
}: FaqLoginButtonProps) => {
  const { buildLink: buildAgendamentoLink } = useAgendamentoNavigation();
  const isLoggedIn = useStore(authStore, (state) => state.computed.isLoggedIn);
  const enableAuth = useStore(
    useFeatureFlagContext,
    (state) => state.enableAuth,
  );

  const { buttonSmFit } = styleFaq();
  const router = useRouter();
  const buttonClassName = className ?? buttonSmFit({ className: 'px-12' });
  const analyticsEvent = AnalyticsEnum.CLICK_FAQ_MY_ONLINE_SIGN_IN_BUTTON;

  function onClickWhenNotLoggedIn() {
    setOpenSignInModal(true, () => {
      router.push('/agendamentos/');
      const logger = getAnalyticsPlugin();
      logger.track(AnalyticsEnum.REDIRECT_FAQ_TO_APPOINTMENT_AFTER_LOGIN);
    });
  }

  if (!enableAuth) {
    return null;
  }

  const useDirectLink = config.stageOneEnabled || isLoggedIn;

  if (useDirectLink) {
    const href = config.stageOneEnabled
      ? buildAgendamentoLink({ path: '/agendamentos' })
      : '/agendamentos/';

    return (
      <Button variant={variant} className={buttonClassName} asChild>
        <Link prefetch={false} href={href} data-analytics-event={analyticsEvent}>
          {label}
        </Link>
      </Button>
    );
  }

  return (
    <Button
      variant={variant}
      className={buttonClassName}
      onClick={onClickWhenNotLoggedIn}
      data-analytics-event={analyticsEvent}
    >
      {label}
    </Button>
  );
};
